// GameRoom.js
import React, { useEffect, useState } from 'react';
import socket from '../socket';
import PlayerList from './PlayerList';
import QuestionForm from './QuestionForm';
import InterpretationForm from './InterpretationForm';
import RoundResult from './RoundResult';
import { formatWithPlaceholders } from '../utils/text';

function GameRoom({
  playerName,
  roomCode,
  onExit,
  language = 'he',
  translations = {},
}) {
  const [players, setPlayers] = useState([]);
  const [stage, setStage] = useState('lobby'); // lobby, ask, interpret, results
  const [askerId, setAskerId] = useState(null);
  const [question, setQuestion] = useState('');
  const [rabiAnswer, setRabiAnswer] = useState('');
  const [interpretationScores, setInterpretationScores] = useState({});
  const [winnerId, setWinnerId] = useState(null);
  const [winnerReason, setWinnerReason] = useState('');
  const [timer, setTimer] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const clientId = localStorage.getItem('clientId');
  const asker = players.find((p) => p.id === askerId);
  const direction = language === 'he' ? 'rtl' : 'ltr';

  const {
    gameRoom: gameRoomCopy = {},
    playerList: playerListCopy = {},
    questionForm: questionFormCopy = {},
    interpretationForm: interpretationCopy = {},
    roundResult: roundResultCopy = {},
    common = {},
  } = translations;

  const fallbackStrings = language === 'he'
    ? {
        exitButton: 'יציאה מהחדר',
        readyButton: 'אני מוכן/ה',
        waitingForOthers: 'ממתין/ה לשחקנים אחרים...',
        ask: {
          youAreAsker: 'את/ה השואל/ת! כתוב/כתבי את השאלה שלך',
          waitingFor: 'ממתינים לשאלה מאת {{name}}',
          questionFrom: 'השאלה של {{name}}:',
          questionGeneric: 'השאלה:',
        },
        interpret: {
          askerLabel: 'השואל/ת: {{name}}',
          rabbiAnswerLabel: 'תשובת הרב:',
        },
      }
    : {
        exitButton: 'Leave Room',
        readyButton: "I'm Ready",
        waitingForOthers: 'Waiting for other players...',
        ask: {
          youAreAsker: "You're the asker! Write your question",
          waitingFor: 'Waiting for a question from {{name}}',
          questionFrom: "{{name}}'s question:",
          questionGeneric: 'Question:',
        },
        interpret: {
          askerLabel: 'Asker: {{name}}',
          rabbiAnswerLabel: "Rabbi's Answer:",
        },
      };

  const exitLabel = gameRoomCopy.exitButton || fallbackStrings.exitButton;
  const readyLabel = gameRoomCopy.readyButton || fallbackStrings.readyButton;
  const waitingLabel = gameRoomCopy.waitingForOthers || fallbackStrings.waitingForOthers;
  const askCopy = { ...fallbackStrings.ask, ...(gameRoomCopy.ask || {}) };
  const interpretCopy = { ...fallbackStrings.interpret, ...(gameRoomCopy.interpret || {}) };

  useEffect(() => {
    socket.on('player-list', setPlayers);

    socket.on('start-round', ({ askerId, players: playerList }) => {
      setAskerId(askerId);
      setPlayers(playerList);
      setQuestion('');
      setRabiAnswer('');
      setInterpretationScores({});
      setWinnerId(null);
      setWinnerReason('');
      setIsReady(false);
      setTimer(null);
      setStage('ask');
    });

    socket.on('question-asked', (q) => {
      setQuestion(q);
    });

    socket.on('rabi-answer', (answer) => {
      setRabiAnswer(answer);
      setStage('interpret');
    });

    socket.on('start-interpretation-timer', (timerPayload) => {
      if (typeof timerPayload === 'number') {
        setTimer({ durationSeconds: timerPayload, startedAt: Date.now() });
      } else {
        setTimer(timerPayload);
      }
    });

    socket.on('interpretations-judged', ({ scores, winnerId, winnerReason }) => {
      setInterpretationScores(scores);
      setWinnerId(winnerId);
      setWinnerReason(winnerReason);
      setTimer(null);
      setStage('results');
    });

    socket.on('score-updated', setPlayers);

    socket.on('connect', () => {
      socket.emit('join-room', roomCode, playerName, clientId);
    });

    socket.emit('join-room', roomCode, playerName, clientId);

    return () => {
      socket.off('player-list');
      socket.off('start-round');
      socket.off('question-asked');
      socket.off('rabi-answer');
      socket.off('start-interpretation-timer');
      socket.off('interpretations-judged');
      socket.off('score-updated');
      socket.off('connect');
    };
  }, [roomCode, playerName, clientId]);

  const handleExit = () => {
    socket.emit('leave-room');
    onExit && onExit();
  };

  // מסך לובי
  if (stage === 'lobby') {
    return (
      <div dir={direction}>
        <button onClick={handleExit}>{exitLabel}</button>
        <PlayerList players={players} copy={playerListCopy} language={language} />
        <button
          onClick={() => {
            socket.emit('player-ready');
            setIsReady(true);
          }}
          disabled={isReady}
        >
          {isReady ? waitingLabel : readyLabel}
        </button>
      </div>
    );
  }

  // שלב השאלה
  if (stage === 'ask') {
    return (
      <div dir={direction}>
        <button onClick={handleExit}>{exitLabel}</button>
        <PlayerList players={players} copy={playerListCopy} language={language} />
        <h2>
          {clientId === askerId
            ? askCopy.youAreAsker
            : formatWithPlaceholders(askCopy.waitingFor, { name: asker ? asker.name : '' })}
        </h2>
        {clientId === askerId ? (
          <QuestionForm
            onSubmit={(q) => {
              setQuestion(q);
              socket.emit('question-submitted', { text: q, language });
            }}
            copy={questionFormCopy}
            language={language}
          />
        ) : (
          question ? (
            <div>
              <h3>
                {asker
                  ? formatWithPlaceholders(askCopy.questionFrom, { name: asker.name })
                  : askCopy.questionGeneric}
              </h3>
              <p><strong>{question}</strong></p>
            </div>
          ) : null
        )}
      </div>
    );
  }

  // שלב הפרשנות
  if (stage === 'interpret') {
    return (
      <div dir={direction}>
        <button onClick={handleExit}>{exitLabel}</button>
        <PlayerList players={players} copy={playerListCopy} language={language} />
        <h2>
          {formatWithPlaceholders(interpretCopy.askerLabel, { name: asker?.name || '' })}
        </h2>
        <p><strong>{question}</strong></p>
        <h2>{interpretCopy.rabbiAnswerLabel}</h2>
        <p><strong>{rabiAnswer}</strong></p>
        <InterpretationForm
          onSubmit={(text) => socket.emit('interpretation-submitted', text)}
          timer={timer}
          copy={interpretationCopy}
          language={language}
        />
      </div>
    );
  }

  // תוצאות
  if (stage === 'results') {
    return (
      <div dir={direction}>
        <button onClick={handleExit}>{exitLabel}</button>
        <PlayerList players={players} copy={playerListCopy} language={language} />
        <RoundResult
          question={question}
          rabiAnswer={rabiAnswer}
          scores={interpretationScores}
          winnerId={winnerId}
          winnerReason={winnerReason}
          players={players}
          socketId={clientId}
          copy={roundResultCopy}
          language={language}
          common={common}
          onReady={() => {
            setStage('lobby');
            setIsReady(true);
            socket.emit('player-ready');
          }}
        />
      </div>
    );
  }

  return null;
}

export default GameRoom;

